// File: views/Landing/Home.jsx

import React from 'react';
import styles from '../../styles/LandingPage.module.css';

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 700);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

/**
 * Home renders a hero section with a video background and gradient overlay.
 * Props:
 *  - active: current active section ID
 *  - onNav: callback to navigate to other sections
 */
export default function Home({ active, onNav }) {
  const isMobile = useIsMobile();
  return (
    <section
      id="home"
      className={`${styles.heroVideoSection} ${styles.sec} ${
        active === 'home' ? styles.active : ''
      }`}
      data-anim
    >
      {/* Video background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className={styles.heroVideo}
      >
        <source src="/BG_Video.webm" type="video/webm" />
        Your browser does not support the video tag.
      </video>
      {/* Gradient overlay */}
      <div className={styles.heroVideoGradient} />
      {/* Hero content */}
      <div className={styles.heroVideoContent}>
        <div
          className="container"
          style={{
            maxWidth: isMobile ? 420 : 700,
            margin: '0 auto',
            padding: isMobile ? '2.2rem 0.5rem 1.5rem' : '4rem 1rem 2.5rem',
            marginTop: isMobile ? 0 : undefined,
          }}
        >
          <div
            style={{
              marginBottom: isMobile ? '1rem' : '1.5rem',
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textAlign: isMobile ? 'center' : 'left',
              display: isMobile ? 'none' : 'block',
            }}
          >
            Facebook Growth & Monetization Experts
          </div>
          <h1
            style={{
              fontSize: isMobile ? '20px' : '2.7rem',
              fontWeight:isMobile ? 400: 800,
              paddingBottom:isMobile ? '25px' : undefined,
              color: '#fff',
              marginBottom: isMobile ? '0.7rem' : '1.1rem',
              lineHeight: 1.15,
              textAlign: isMobile ? 'center' : 'left',
            }}
          >
            Grow, Engage, and Monetize Your Facebook Audience
          </h1>
          <p
            style={{
              fontSize: isMobile ? '1.05rem' : '1.25rem',
              color: '#ccc',
              marginBottom: isMobile ? '1.5rem' : '2.2rem',
              lineHeight: 1.5,
              fontWeight: 500,
              textAlign: isMobile ? 'center' : 'left',
              display: isMobile ? 'none' : 'block',
            }}
          >
            Musuko Media helps creators, brands, and artists turn Facebook pages into revenue engines. Let us handle your growth, engagement, and monetization—so you can focus on what you do best.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: isMobile ? 'center' : 'flex-start',
              marginBottom: isMobile ? '1.7rem' : '2.5rem',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <button
              className="btn-white fb-glow-border"
              style={{
                fontSize: isMobile ? '1.05rem' : '1.15rem',
                padding: isMobile ? '1em 0' : '1.1em 2.8em',
                fontWeight: 700,
                letterSpacing: '0.03em',
                boxShadow: '0 4px 24px rgba(24,119,242,0.10)',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? 400 : undefined,
              }}
              onClick={() => onNav('contact')}
            >
              Book a Call
            </button>
          </div>
          {/* Metrics: stack vertically on mobile, horizontal on desktop */}
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'center',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? '1.1rem' : '2.5rem',
              marginTop: isMobile ? '1.5rem' : '2.5rem',
              width: '100%',
            }}
          >
            <div style={{ textAlign: 'center', background: isMobile ? 'rgba(255,255,255,0.07)' : 'none', borderRadius: isMobile ? 12 : 0, padding: isMobile ? '0.7rem 0' : 0 }}>
              <div style={{ fontSize: isMobile ? '1.35rem' : '2rem', fontWeight: 700, marginBottom: 2,
                background: 'linear-gradient(90deg, #1877f2 0%, #42a5f5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
              }}>12+</div>
              <div style={{ fontSize: isMobile ? '0.98rem' : '1rem', color: '#ccc', fontWeight: 500 }}>Years Experience</div>
            </div>
            <div style={{ textAlign: 'center', background: isMobile ? 'rgba(255,255,255,0.07)' : 'none', borderRadius: isMobile ? 12 : 0, padding: isMobile ? '0.7rem 0' : 0 }}>
              <div style={{ fontSize: isMobile ? '1.35rem' : '2rem', fontWeight: 700, marginBottom: 2,
                background: 'linear-gradient(90deg, #1877f2 0%, #42a5f5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
              }}>250+</div>
              <div style={{ fontSize: isMobile ? '0.98rem' : '1rem', color: '#ccc', fontWeight: 500 }}>Clients Trust Us</div>
            </div>
            <div style={{ textAlign: 'center', background: isMobile ? 'rgba(255,255,255,0.07)' : 'none', borderRadius: isMobile ? 12 : 0, padding: isMobile ? '0.7rem 0' : 0 }}>
              <div style={{ fontSize: isMobile ? '1.35rem' : '2rem', fontWeight: 700, marginBottom: 2,
                background: 'linear-gradient(90deg, #1877f2 0%, #42a5f5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
              }}>5B+</div>
              <div style={{ fontSize: isMobile ? '0.98rem' : '1rem', color: '#ccc', fontWeight: 500 }}>Views Generated</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
